import { useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import type { Trade as BaseTrade } from '../../types';
import { useImportedTradesRecords } from '../../hooks/useImportedTrades';
import { matchesTradeSearch } from './tradesFilters';
import { buildTenantStorageKey, readTenantStorageItem } from '../../lib/storage/tenantStorage';
import { writePrivateTradingJson } from '../../lib/storage/privateTradingStorage';

type SearchScope = 'All' | 'Trades' | 'Notes' | 'Tags';
type SearchTrade = BaseTrade & { isCustom?: boolean };

type NoteMatch = {
    kind: 'note';
    date: string;
    snippet: string;
    name?: string;
    id?: string;
    symbol?: string;
};

type TagMatch = {
    kind: 'tag';
    date: string;
    tags: string[];
};

const NOTES_KEY = 'journal.notes.byDate.v1';
const NOTES_HISTORY_KEY = 'journal.notes.history.v1';
const TAGS_KEY = 'journal.tags.byDate.v1';

const loadFromStorage = <T,>(key: string, fallback: T) => {
    if (typeof window === 'undefined') return fallback;
    try {
        const raw = readTenantStorageItem(buildTenantStorageKey(key), [key]);
        return raw ? JSON.parse(raw) as T : fallback;
    } catch {
        return fallback;
    }
};

const stripHtml = (html?: string) => {
    if (!html) return '';
    try {
        return html.replace(/<[^>]*>/g, ' ');
    } catch {
        return html;
    }
};

const formatMoney = (value?: number) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return '';
    const absolute = Math.abs(value).toFixed(2);
    return `${value < 0 ? '-' : ''}$${absolute}`;
};

const SearchIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="7" />
        <path d="m21 21-4.3-4.3" />
    </svg>
);

const CloseIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <path d="M6 6l12 12" />
        <path d="M18 6 6 18" />
    </svg>
);

const ActivityIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12h4l2-6 4 12 2-6h6" />
    </svg>
);

const HistoryIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12a9 9 0 1 0 3-6.7" />
        <path d="M3 4v6h6" />
        <path d="M12 7v5l3 2" />
    </svg>
);

const NotebookIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="6" y="3" width="13" height="18" rx="2" />
        <path d="M9 3v18" />
        <path d="M10.5 8h5" />
        <path d="M10.5 12h5" />
    </svg>
);

const TagIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 13.5 11.5 22 2 12.5V3h9.5L20 11.5z" />
        <circle cx="8.5" cy="8.5" r="1.25" fill="currentColor" stroke="none" />
    </svg>
);

const FilterIcon = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 5h16l-6 7v5l-4 2v-7z" />
    </svg>
);

function formatTradeLabel(trade: SearchTrade) {
    if (!trade.date) return trade.symbol;
    return `${trade.symbol} • ${trade.date}`;
}

function highlightQuerySource(query: string) {
    return query.trim() ? `"${query.trim()}"` : '""';
}

export function SearchPage() {
    const [searchType, setSearchType] = useState<SearchScope>('All');
    const [query, setQuery] = useState('');
    const [ignoreGlobalFilters, setIgnoreGlobalFilters] = useState(true);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [isDark, setIsDark] = useState<boolean>(() => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));

    useEffect(() => {
        if (typeof document === 'undefined') return;
        const update = () => setIsDark(document.documentElement.classList.contains('dark'));
        const obs = new MutationObserver(() => update());
        try {
            obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        } catch {
            // ignore in non-browser environments
        }
        // sync initial value
        update();
        return () => { try { obs.disconnect(); } catch {} };
    }, []);

    useEffect(() => {
        const handler = (event: Event) => {
            try {
                const nextQuery = String((event as CustomEvent).detail || '');
                setQuery(nextQuery);
                window.setTimeout(() => {
                    try {
                        inputRef.current?.focus();
                    } catch {
                        // ignore focus failures
                    }
                }, 60);
            } catch {
                // ignore malformed events
            }
        };

        try {
            window.addEventListener('tt:openSearch', handler as EventListener);
        } catch {
            // ignore non-browser environments
        }

        return () => {
            try {
                window.removeEventListener('tt:openSearch', handler as EventListener);
            } catch {
                // ignore cleanup failures
            }
        };
    }, []);

    useEffect(() => {
        const id = window.setTimeout(() => {
            try {
                inputRef.current?.focus();
            } catch {
                // ignore autofocus failures
            }
        }, 100);

        return () => window.clearTimeout(id);
    }, []);

    const importedTrades = useImportedTradesRecords();
    const realTrades = useMemo<SearchTrade[]>(() => importedTrades.map((trade) => ({ ...(trade as unknown as SearchTrade), isCustom: true })), [importedTrades]);

    const notesByDate = useMemo<Record<string, string>>(() => loadFromStorage<Record<string, string>>(NOTES_KEY, {}), []);
    const notesHistory = useMemo<Record<string, { id: string; text: string; ts: number; name?: string; symbol?: string }[]>>(
        () => loadFromStorage<Record<string, { id: string; text: string; ts: number; name?: string; symbol?: string }[]>>(NOTES_HISTORY_KEY, {}),
        [],
    );
    const tagsByDate = useMemo<Record<string, string[]>>(() => loadFromStorage<Record<string, string[]>>(TAGS_KEY, {}), []);

    const normalizedQuery = query.trim().toLowerCase();
    const parsedQueryDate = useMemo(() => {
        const trimmed = query.trim();
        if (!trimmed) return null;

        const isoMatch = trimmed.match(/\d{4}-\d{2}-\d{2}/);
        if (isoMatch) return isoMatch[0];

        const parsed = new Date(trimmed);
        if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);

        return null;
    }, [query]);

    const tradeResults = useMemo(() => {
        if (!normalizedQuery) return [] as SearchTrade[];
        // Search only real imported/authoritative trade records; no simulated rows.
        return realTrades.filter((trade) => {
            const dateKey = String(trade.date || '').slice(0, 10);
            const matchesDate = parsedQueryDate ? dateKey === parsedQueryDate : false;
            const matchesPnl = formatMoney(trade.pnl).toLowerCase().includes(normalizedQuery);
            const matchesVolume = String(trade.volume ?? '').includes(normalizedQuery);
            const matchesSide = (trade.side || '').toLowerCase().includes(normalizedQuery);
            const matchesTrade = matchesTradeSearch(trade, query);
            const matchesNotes = stripHtml(notesByDate[dateKey]).toLowerCase().includes(normalizedQuery)
                || Object.values(notesHistory).flat().some((entry) => {
                    const text = `${entry.name || ''} ${stripHtml(entry.text || '')}`.trim().toLowerCase();
                    return text.includes(normalizedQuery) || (parsedQueryDate && dateKey === parsedQueryDate);
                });
            const matchesTags = (tagsByDate[dateKey] || []).some((tag) => tag.toLowerCase().includes(normalizedQuery));

            return matchesDate || matchesTrade || matchesPnl || matchesVolume || matchesSide || matchesNotes || matchesTags;
        });
    }, [notesByDate, notesHistory, parsedQueryDate, query, realTrades, normalizedQuery, tagsByDate]);

    const noteResults = useMemo<NoteMatch[]>(() => {
        if (!normalizedQuery) return [];

        const matches: NoteMatch[] = [];

        for (const date of Object.keys(notesByDate)) {
            const text = stripHtml(notesByDate[date]);
            if (text.toLowerCase().includes(normalizedQuery) || (parsedQueryDate && date === parsedQueryDate)) {
                matches.push({ kind: 'note', date, snippet: text.slice(0, 220) });
            }
        }

        for (const date of Object.keys(notesHistory)) {
            const historyEntries = notesHistory[date] || [];
            for (const entry of historyEntries) {
                const text = `${entry.name || ''} ${stripHtml(entry.text || '')}`.trim();
                if (text.toLowerCase().includes(normalizedQuery) || (parsedQueryDate && date === parsedQueryDate)) {
                    matches.push({ kind: 'note', date, snippet: stripHtml(entry.text).slice(0, 220), name: entry.name, id: entry.id, symbol: (entry as unknown as { symbol?: string }).symbol });
                }
            }
        }

        return matches;
    }, [notesByDate, notesHistory, normalizedQuery, parsedQueryDate]);

    const tagResults = useMemo<TagMatch[]>(() => {
        if (!normalizedQuery) return [];

        const matches: TagMatch[] = [];
        for (const date of Object.keys(tagsByDate)) {
            const tags = tagsByDate[date] || [];
            if (tags.some((tag) => tag.toLowerCase().includes(normalizedQuery)) || (parsedQueryDate && date === parsedQueryDate)) {
                matches.push({ kind: 'tag', date, tags });
            }
        }

        return matches;
    }, [normalizedQuery, parsedQueryDate, tagsByDate]);

    // Navigation helpers: open a Trade Details view by setting session storage
    // and using the global navigation helper used elsewhere in the app.
    const navigateToTrade = (trade: SearchTrade | null) => {
        if (!trade) return;
        try { writePrivateTradingJson('last-view-trade', JSON.stringify(trade), 'session'); } catch {}
        try {
            const win = window as unknown as { __TT_navigate?: (s: string) => void };
            if (win.__TT_navigate) win.__TT_navigate('Trade Details');
            else window.history.pushState({}, '', window.location.href);
        } catch {}
    };

    const findTradeByDateAndSymbol = (date?: string, symbol?: string) => {
        if (!date) return null;
        // Prefer real/manual trades when resolving notes/tags to a trade.
        if (symbol) {
            const exact = realTrades.find(t => t.date === date && String(t.symbol) === String(symbol));
            if (exact) return exact;
        }
        const first = realTrades.find(t => t.date === date);
        return first ?? null;
    };

    const visibleTrades = searchType === 'All' || searchType === 'Trades';
    const visibleNotes = searchType === 'All' || searchType === 'Notes';
    const visibleTags = searchType === 'All' || searchType === 'Tags';
    const totalMatches = tradeResults.length + noteResults.length + tagResults.length;

    const closeSearch = () => {
        try {
            const win = window as unknown as { __TT_navigate?: (s: string) => void };
            if (win.__TT_navigate) {
                win.__TT_navigate('Dashboard');
            } else {
                window.history.back();
            }
        } catch {
            try {
                window.history.back();
            } catch {
                // ignore navigation failures
            }
        }
    };

    return (
        <div className={`min-h-screen px-4 py-4 ${isDark ? 'text-zinc-100' : 'text-zinc-900'} sm:px-6 sm:py-6 lg:px-8`}>
                <div className="mx-auto flex w-full max-w-3xl items-start justify-center pt-6">
                <div className={`relative w-full overflow-hidden rounded-lg border ${isDark ? 'border-zinc-100/10 shadow-[0_24px_60px_rgba(0,0,0,0.55)] bg-zinc-900/40' : 'border-zinc-200 shadow-[0_12px_30px_rgba(16,24,40,0.06)] bg-white'}`}>

                    <header className={`relative border-b px-5 py-6 text-center sm:px-8 sm:py-7 ${isDark ? 'border-zinc-100/10 bg-zinc-900 text-white' : 'border-zinc-200 bg-zinc-200 text-zinc-900'}`}>
                        <button
                            type="button"
                            aria-label="Close search"
                            onClick={closeSearch}
                            className={`absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-2xl border ${isDark ? 'border-rose-500/20 bg-rose-500/10 text-rose-300 hover:border-rose-400/30 hover:bg-rose-500/20 hover:text-rose-200' : 'border-rose-200 bg-rose-50 text-rose-600 hover:border-rose-300/60 hover:bg-rose-50'}`}
                        >
                            <CloseIcon className="h-4 w-4" />
                        </button>

                        <div className="mx-auto max-w-3xl space-y-2">
                            <h1 className="text-2xl font-semibold tracking-tight sm:text-[2rem]"><span className={`inline-block px-3 py-1 rounded-md ${isDark ? 'bg-zinc-900 text-white' : ' text-zinc-900'}`}>Search Trade Intel</span></h1>
                            <p className={`text-sm sm:text-base ${isDark ? 'text-zinc-400' : 'text-zinc-700'}`}>Instant access to your archived trades, notes, and comments.</p>
                        </div>
                    </header>

                    <div className="space-y-5 px-5 py-6 sm:px-8 sm:py-7">
                        <div className="relative">
                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                                <SearchIcon className="h-5 w-5 text-blue-300/80" />
                            </div>
                            <input
                                ref={inputRef}
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Escape') {
                                        closeSearch();
                                    }
                                }}
                                placeholder="Symbols, dates (YYYY-MM-DD), notes, or tags..."
                                className={`h-16 w-full rounded-2xl border pl-14 pr-4 text-base outline-none transition placeholder:text-zinc-500 focus:border-blue-400/60 focus:ring-2 focus:ring-blue-500/20 ${isDark ? 'border-zinc-100/10 bg-zinc-900/90 text-zinc-100' : 'border-zinc-200 bg-white text-zinc-900'}`}
                            />
                        </div>

                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                            <div className={`inline-flex w-full flex-wrap overflow-hidden rounded-2xl border p-1 shadow-inner ${isDark ? 'border-zinc-100/10 bg-zinc-900/85 shadow-black/20' : 'border-zinc-200 bg-white/50 shadow-none'} sm:w-auto`}>
                                {[
                                    { key: 'All' as const, label: 'All', icon: ActivityIcon },
                                    { key: 'Trades' as const, label: 'Trades', icon: HistoryIcon },
                                    { key: 'Notes' as const, label: 'Notes', icon: NotebookIcon },
                                    { key: 'Tags' as const, label: 'Tags', icon: TagIcon },
                                ].map(({ key, label, icon: Icon }) => {
                                    const active = searchType === key;
                                            return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => setSearchType(key)}
                                                className={`flex min-w-[110px] items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium transition ${active ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : (isDark ? 'text-zinc-400 hover:bg-white/5 hover:text-zinc-200' : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900')}`}
                                        >
                                            <Icon className="h-4 w-4" />
                                            <span>{label}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                type="button"
                                aria-pressed={ignoreGlobalFilters}
                                onClick={() => setIgnoreGlobalFilters((current) => !current)}
                                className={`inline-flex h-11 items-center justify-center gap-2 rounded-xl border px-4 text-sm font-medium transition ${ignoreGlobalFilters ? (isDark ? 'border-zinc-100/10 bg-zinc-900/80 text-zinc-200' : 'border-zinc-200 bg-white text-zinc-900') : (isDark ? 'border-zinc-100/10 bg-white/5 text-zinc-400 hover:text-zinc-200' : 'border-zinc-200 bg-white/5 text-zinc-700 hover:text-zinc-900')}`}
                            >
                                <FilterIcon className="h-4 w-4" />
                                <span>Ignore Global Filters</span>
                            </button>
                        </div>

                        <div className={`rounded-2xl border px-4 py-4 sm:px-5 ${isDark ? 'border-zinc-100/10 bg-white/[0.03]' : 'border-zinc-200 bg-white'}`}>
                            <div className={`flex items-start gap-3 text-sm ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                                <div className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${isDark ? 'border-blue-400/30 bg-blue-500/10 text-blue-300' : 'border-blue-100 bg-blue-50 text-blue-600'}`}>
                                    <span className="text-xs font-semibold">i</span>
                                </div>
                                <p className={`leading-6 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                                    {query.trim()
                                        ? <>Searching archive for <span className={`font-medium ${isDark ? 'text-white' : 'text-zinc-900'}`}>{highlightQuerySource(query)}</span>. Search matches symbols, PnL, manual notes, and automated history logs.</>
                                        : 'Start typing to search your archived trades, notes, and tags.'}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className={`border-t px-5 py-6 sm:px-8 sm:py-8 ${isDark ? 'border-zinc-100/10' : 'border-zinc-200'}`}>
                        {!query.trim() ? (
                            <div className={`flex min-h-[320px] flex-col items-center justify-center rounded-none border border-dashed px-6 py-16 text-center ${isDark ? 'border-zinc-100/10 bg-black/20' : 'border-zinc-200 bg-white/50'}`}>
                                <div className={`mb-5 inline-flex h-20 w-20 items-center justify-center rounded-full border ${isDark ? 'border-blue-400/15 bg-blue-500/10 text-blue-300' : 'border-blue-100 bg-blue-50 text-blue-600'}`}>
                                    <SearchIcon className="h-10 w-10" />
                                </div>
                                <p className={`${isDark ? 'text-blue-200/45' : 'text-blue-700/60'} text-lg italic`}>Type above to begin searching your trade data...</p>
                            </div>
                        ) : totalMatches === 0 ? (
                            <div className={`flex min-h-[320px] flex-col items-center justify-center rounded-none border px-6 py-16 text-center ${isDark ? 'border-zinc-100/10 bg-black/20' : 'border-zinc-200 bg-white/50'}`}>
                                <div className={`mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full border ${isDark ? 'border-zinc-100/10 bg-white/5 text-zinc-400' : 'border-zinc-200 bg-white text-zinc-700'}`}>
                                    <SearchIcon className="h-8 w-8" />
                                </div>
                                <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-zinc-900'}`}>No matches found</h2>
                                <p className={`mt-2 max-w-xl text-sm leading-6 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                                    Try a symbol, a date like 2026-03-30, a note snippet, or a tag name.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {visibleTrades && (
                                    <section className="space-y-3">
                                        <div className="flex items-center justify-between gap-4">
                                            <h2 className={`text-sm font-semibold uppercase tracking-[0.24em] ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Trades</h2>
                                            <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-600'}`}>{tradeResults.length} matches</span>
                                        </div>
                                        <div className="grid gap-3">
                                            {tradeResults.slice(0, 12).map((trade) => {
                                                const pnlPositive = (trade.pnl ?? 0) >= 0;
                                                return (
                                                    <article
                                                        key={`${trade.id}-${trade.date || 'no-date'}`}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') navigateToTrade(trade); }}
                                                        onClick={() => navigateToTrade(trade)}
                                                        className={`cursor-pointer rounded-2xl border px-4 py-4 transition ${isDark ? 'border-zinc-100/10 bg-zinc-900/80 hover:border-zinc-100/20 hover:bg-zinc-900' : 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-white/50'}`}
                                                    >
                                                        <div className="flex items-start justify-between gap-4">
                                                            <div>
                                                                <div className={`text-base font-semibold ${isDark ? 'text-white' : 'text-zinc-900'}`}>{formatTradeLabel(trade)}</div>
                                                                <div className={`mt-1 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                                                                    {(trade.isCustom ? 'Manual entry' : 'Auto-generated')}{trade.side ? ` • ${trade.side}` : ''}{trade.volume ? ` • Volume ${trade.volume}` : ''}
                                                                </div>
                                                            </div>
                                                            <div className={`text-sm font-semibold ${pnlPositive ? 'text-emerald-300' : 'text-rose-300'}`}>
                                                                {formatMoney(trade.pnl)}
                                                            </div>
                                                        </div>
                                                    </article>
                                                );
                                            })}
                                        </div>
                                    </section>
                                )}

                                {visibleNotes && (
                                    <section className="space-y-3">
                                        <div className="flex items-center justify-between gap-4">
                                            <h2 className={`text-sm font-semibold uppercase tracking-[0.24em] ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Notes</h2>
                                            <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-600'}`}>{noteResults.length} matches</span>
                                        </div>
                                        <div className="grid gap-3">
                                            {noteResults.slice(0, 10).map((note, index) => (
                                                <article
                                                    key={`${note.id || note.date}-${index}`}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') { const t = findTradeByDateAndSymbol(note.date, note.symbol); if (t) navigateToTrade(t); } }}
                                                    onClick={() => { const t = findTradeByDateAndSymbol(note.date, note.symbol); if (t) navigateToTrade(t); }}
                                                    className={`cursor-pointer rounded-2xl border px-4 py-4 transition ${isDark ? 'border-zinc-100/10 bg-zinc-900/80 hover:border-zinc-100/20 hover:bg-zinc-900' : 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-white/50'}`}
                                                >
                                                    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{note.date}</div>
                                                    {note.name ? <div className={`mt-1 text-sm font-semibold ${isDark ? 'text-white' : 'text-zinc-900'}`}>{note.name}</div> : null}
                                                    <p className={`mt-2 text-sm leading-6 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{note.snippet}</p>
                                                </article>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {visibleTags && (
                                    <section className="space-y-3">
                                        <div className="flex items-center justify-between gap-4">
                                            <h2 className={`text-sm font-semibold uppercase tracking-[0.24em] ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Tags</h2>
                                            <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-600'}`}>{tagResults.length} matches</span>
                                        </div>
                                        <div className="grid gap-3">
                                            {tagResults.slice(0, 10).map((tagMatch) => (
                                                <article
                                                    key={`${tagMatch.date}-${tagMatch.tags.join('|')}`}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') { const t = findTradeByDateAndSymbol(tagMatch.date); if (t) navigateToTrade(t); } }}
                                                    onClick={() => { const t = findTradeByDateAndSymbol(tagMatch.date); if (t) navigateToTrade(t); }}
                                                    className={`cursor-pointer rounded-2xl border px-4 py-4 transition ${isDark ? 'border-zinc-100/10 bg-zinc-900/80 hover:border-zinc-100/20 hover:bg-zinc-900' : 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-white/50'}`}
                                                >
                                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                                        <div>
                                                            <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-zinc-900'}`}>{tagMatch.date}</div>
                                                            <div className={`mt-1 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Tagged archive entries</div>
                                                        </div>
                                                        <div className="flex flex-wrap gap-2">
                                                            {tagMatch.tags.slice(0, 6).map((tag) => (
                                                                <span key={tag} className={`rounded-full border px-3 py-1 text-xs font-medium ${isDark ? 'border-blue-400/15 bg-blue-500/10 text-blue-200' : 'border-blue-100 bg-blue-50 text-blue-700'}`}>
                                                                    {tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    </section>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
